export const RECORDER_OPEN_EVENT = 'notizy:recorder-open';
export const RECORDER_COMPLETE_EVENT = 'notizy:recorder-complete';
export const RECORDER_STATUS_EVENT = 'notizy:recorder-status';
export const RECORDING_UPDATED_EVENT = 'notizy:recording-updated';

export interface RecorderStatusDetail {
    isRecording: boolean;
    isPaused: boolean;
    isUploading: boolean;
    uploadProgress: number;
    recordingTime: number;
    statusMessage: string;
}

export interface RecorderCompleteDetail {
    fileId: string;
}

export interface RecordingUpdatedDetail {
    fileId: string;
    name?: string;
}

declare global {
    interface Window {
        __notizyRecorderStatus?: RecorderStatusDetail;
    }
}

const dispatchCustomEvent = <T>(eventName: string, detail?: T) => {
    if (typeof window === 'undefined') {
        return;
    }

    const event = new CustomEvent<T>(eventName, {
        detail
    });

    window.dispatchEvent(event);
};

export const dispatchRecorderOpen = () => {
    dispatchCustomEvent(RECORDER_OPEN_EVENT);
};

export const dispatchRecorderComplete = (fileId: string) => {
    dispatchCustomEvent<RecorderCompleteDetail>(RECORDER_COMPLETE_EVENT, { fileId });
};

export const dispatchRecordingUpdated = (fileId: string, name?: string) => {
    dispatchCustomEvent<RecordingUpdatedDetail>(RECORDING_UPDATED_EVENT, { fileId, name });
};

export const dispatchRecorderStatus = (detail: RecorderStatusDetail) => {
    if (typeof window !== 'undefined') {
        window.__notizyRecorderStatus = { ...detail };
    }
    dispatchCustomEvent<RecorderStatusDetail>(RECORDER_STATUS_EVENT, detail);
};

export const getRecorderStatusSnapshot = (): RecorderStatusDetail | null => {
    if (typeof window === 'undefined') {
        return null;
    }

    return window.__notizyRecorderStatus ?? null;
};

/**
 * De recorder is "bezig" zolang er een opname loopt, gepauzeerd is of nog
 * geüpload wordt. In al die gevallen mag de pagina niet herladen worden: een
 * reload gooit de lopende opname weg.
 */
export const isRecorderBusy = (): boolean => {
    const status = getRecorderStatusSnapshot();
    return Boolean(status?.isRecording || status?.isPaused || status?.isUploading);
};

/**
 * Wacht tot de recorder klaar is. Resolvet direct als hij nu al idle is.
 * Bedoeld voor acties die moeten wachten op de opname (zoals een update-reload).
 */
export const whenRecorderIdle = (): Promise<void> => {
    if (typeof window === 'undefined' || !isRecorderBusy()) {
        return Promise.resolve();
    }

    return new Promise((resolve) => {
        const handleStatus = () => {
            if (isRecorderBusy()) {
                return;
            }
            window.removeEventListener(RECORDER_STATUS_EVENT, handleStatus);
            resolve();
        };

        window.addEventListener(RECORDER_STATUS_EVENT, handleStatus);
    });
};
